///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Flat-shaded unit meshes for the 3D buoy marker, built once per GL
/// context and scaled per station. All sit in a local frame with +Y up at
/// the station and +Z north; directional pieces point along +Z and are
/// rotated about +Y to their compass bearing. Faces carry their own
/// normals (vertices are not shared) so the solids read as crisp facets.
///

import { type cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

type Vec = [number, number, number]

class FacetMesh implements Geometry {
	public position: VertexAttribute
	public normal: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public available: boolean = true
	public bounds!: cube
	public boundingSphere!: sphere

	constructor(context: StovokorContext) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal])
	}

	/** Counter-clockwise when seen from outside */
	public triangle(a: Vec, b: Vec, c: Vec): void {
		const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
		const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
		let nx = uy * vz - uz * vy
		let ny = uz * vx - ux * vz
		let nz = ux * vy - uy * vx
		const len = Math.hypot(nx, ny, nz) || 1
		nx /= len; ny /= len; nz /= len
		const base = this.position.count
		for (const p of [a, b, c]) {
			this.position.addPos(p[0], p[1], p[2])
			this.normal.add(nx, ny, nz)
		}
		this.indexBuffer.triangle(base, base + 1, base + 2)
	}

	public quad(a: Vec, b: Vec, c: Vec, d: Vec): void {
		this.triangle(a, b, c)
		this.triangle(a, c, d)
	}

	public finish(): this {
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
		return this
	}

	public destroy(): void {
		// Shared for the life of the context
	}
}

/**
 * Swell wedge: a low ridge on the surface from z = 0.4 (inside the dome)
 * tapering to a point at z = 2.0, half-width 0.6 at the base, 0.3 high.
 * Unit = dome radius.
 */
function buildWedge(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	const z0 = 0.4, z1 = 2.0, hw = 0.6, h = 0.3
	const bl: Vec = [-hw, 0, z0], br: Vec = [hw, 0, z0], tip: Vec = [0, 0, z1]
	const ridge0: Vec = [0, h, z0]
	// Two sloping faces meeting at the ridge, a back face and a floor
	m.triangle(br, ridge0, tip)
	m.triangle(bl, tip, ridge0)
	m.triangle(br, bl, ridge0)
	m.triangle(bl, br, tip)
	return m.finish()
}

/** Torus in the XZ plane, major radius 1, tube radius `tube` */
function buildTorus(context: StovokorContext, tube: number, segments: number = 32, sides: number = 8): FacetMesh {
	const m = new FacetMesh(context)
	const point = (i: number, j: number): Vec => {
		const u = (i / segments) * Math.PI * 2
		const v = (j / sides) * Math.PI * 2
		const r = 1 + tube * Math.cos(v)
		return [r * Math.sin(u), tube * Math.sin(v), r * Math.cos(u)]
	}
	for (let i = 0; i < segments; i++) {
		for (let j = 0; j < sides; j++) {
			m.quad(point(i, j), point(i + 1, j), point(i + 1, j + 1), point(i, j + 1))
		}
	}
	return m.finish()
}

/** Box from z = 0 to z = 1, square cross-section of half-size 0.5, resting on y = 0 */
function buildShaft(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	const x = 0.5, y0 = 0, y1 = 1
	const c = (px: number, py: number, pz: number): Vec => [px, py, pz]
	m.quad(c(-x, y0, 0), c(x, y0, 0), c(x, y1, 0), c(-x, y1, 0))       // back (z=0)
	m.quad(c(x, y0, 1), c(-x, y0, 1), c(-x, y1, 1), c(x, y1, 1))       // front (z=1)
	m.quad(c(-x, y1, 0), c(x, y1, 0), c(x, y1, 1), c(-x, y1, 1))       // top
	m.quad(c(-x, y0, 1), c(x, y0, 1), c(x, y0, 0), c(-x, y0, 0))       // bottom
	m.quad(c(x, y0, 0), c(x, y0, 1), c(x, y1, 1), c(x, y1, 0))         // +x
	m.quad(c(-x, y0, 1), c(-x, y0, 0), c(-x, y1, 0), c(-x, y1, 1))     // -x
	return m.finish()
}

/** Cone: base radius 1 on y = 0, apex at y = 1.3 */
function buildCone(context: StovokorContext, segments: number = 20): FacetMesh {
	const m = new FacetMesh(context)
	const apex: Vec = [0, 1.3, 0]
	const center: Vec = [0, 0, 0]
	for (let i = 0; i < segments; i++) {
		const a0 = (i / segments) * Math.PI * 2
		const a1 = ((i + 1) / segments) * Math.PI * 2
		const p0: Vec = [Math.sin(a0), 0, Math.cos(a0)]
		const p1: Vec = [Math.sin(a1), 0, Math.cos(a1)]
		m.triangle(p0, p1, apex)
		m.triangle(p1, p0, center)
	}
	return m.finish()
}

/** Octahedron with unit radius, points on the axes */
function buildOctahedron(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	const top: Vec = [0, 1, 0], bottom: Vec = [0, -1, 0]
	const ring: Vec[] = [[0, 0, 1], [1, 0, 0], [0, 0, -1], [-1, 0, 0]]
	for (let i = 0; i < 4; i++) {
		const a = ring[i], b = ring[(i + 1) % 4]
		m.triangle(a, b, top)
		m.triangle(b, a, bottom)
	}
	return m.finish()
}

export interface BuoyMarkerMeshes {
	wedge: Geometry
	/** Thin / medium / thick period rings */
	rings: [Geometry, Geometry, Geometry]
	shaft: Geometry
	cone: Geometry
	octahedron: Geometry
}

const sharedMeshes = new WeakMap<StovokorContext, BuoyMarkerMeshes>()

export function sharedBuoyMarkerMeshes(context: StovokorContext): BuoyMarkerMeshes {
	let meshes = sharedMeshes.get(context)
	if (!meshes) {
		meshes = {
			wedge: buildWedge(context),
			rings: [buildTorus(context, 0.07), buildTorus(context, 0.13), buildTorus(context, 0.22)],
			shaft: buildShaft(context),
			cone: buildCone(context),
			octahedron: buildOctahedron(context),
		}
		sharedMeshes.set(context, meshes)
	}
	return meshes
}
